/**
 * Trace de calcul : rééchantillonnage, traitement de l'altitude, pentes et D+.
 * EF-GPX-002, EF-GPX-003, EF-GPX-004, annexe A §A.6.
 *
 * La trace est le plus gros objet du modèle — environ 15 000 points pour
 * 1 500 km. Elle est représentée par des tableaux typés parallèles, jamais par
 * un tableau d'objets. `NaN` marque l'altitude absente plutôt que `null`, ce
 * qui conserve la structure homogène.
 */

import { distance, interpolate, type Coordinate } from './geodesy';

/** Pas de rééchantillonnage, en mètres. Valeur fixe de l'annexe A. */
export const RESAMPLE_STEP_METERS = 100;

/** Altitudes brutes hors de cet intervalle sont retirées (étape 1). */
export const MIN_PLAUSIBLE_ALTITUDE = -500;
export const MAX_PLAUSIBLE_ALTITUDE = 9000;

/** Portée maximale d'une interpolation d'altitude, en mètres de parcours. */
export const MAX_ALTITUDE_INTERPOLATION_METERS = 1000;

/** Écart d'altitude au-delà duquel un point isolé est un pic (étape 3). */
export const PEAK_THRESHOLD_METERS = 30;

/** Pente bornée à cet intervalle, en pourcentage (étape 6). */
export const MAX_ABSOLUTE_GRADE_PERCENT = 30;

/** Seuil de couverture d'altitude, en pourcentage (EF-GPX-003). */
export const MIN_ELEVATION_COVERAGE_PERCENT = 95;

/** Trou d'altitude maximal admis, en mètres de parcours (EF-GPX-003). */
export const MAX_ELEVATION_HOLE_METERS = 2000;

/**
 * Écart entre deux segments au-delà duquel la jonction est traitée comme un
 * écart au sens de EF-GPX-002 : avertissement, altitude absente, D+ non compté.
 * En deçà, deux segments consécutifs sont considérés comme contigus.
 */
export const SEGMENT_GAP_THRESHOLD_METERS = 1000;

/** Écart entre deux points consécutifs d'un même segment signalé sans refus. */
export const POINT_ANOMALY_THRESHOLD_METERS = 10000;

export interface ProgressionRange {
  readonly fromMeters: number;
  readonly toMeters: number;
}

/** Écart conservé dans `route.segmentGaps` / `route.pointAnomalies`. */
export interface DistanceAnomaly {
  readonly afterProgressionMeters: number;
  readonly distanceMeters: number;
}

/** Points bruts du parcours sélectionné, segments déjà concaténés. */
export interface RawTrack {
  readonly lat: Float64Array;
  readonly lon: Float64Array;
  /** `NaN` lorsque le point ne porte pas d'altitude. */
  readonly ele: Float64Array;
  /** Indices de premier point de chaque segment. Toujours `[0, …]`. */
  readonly segmentStarts: readonly number[];
  readonly count: number;
}

export interface ElevationSummary {
  readonly available: boolean;
  readonly coveragePercent: number;
  readonly maxGapMeters: number;
  readonly interpolatedPointCount: number;
  readonly filteredPointCount: number;
}

export interface ComputedTrack {
  readonly lat: Float64Array;
  readonly lon: Float64Array;
  /** `NaN` quand l'altitude est absente. */
  readonly alt: Float64Array;
  /** Progression cumulée, en mètres. Dérivée, jamais exportée. */
  readonly cumulativeDistance: Float64Array;
  /** D+ cumulé, en mètres. Dérivé, jamais exporté. */
  readonly cumulativeGain: Float64Array;
  /** Pente de chaque point, en pourcentage, bornée à ±30. */
  readonly grade: Float64Array;
  readonly count: number;
  readonly lengthMeters: number;
  readonly gapRanges: readonly ProgressionRange[];
  readonly segmentGaps: readonly DistanceAnomaly[];
  readonly pointAnomalies: readonly DistanceAnomaly[];
  readonly elevation: ElevationSummary;
  /** `null` lorsque l'altitude est indisponible au sens de EF-GPX-003. */
  readonly elevationGainMeters: number | null;
}

export function coordinateAt(track: ComputedTrack, index: number): Coordinate {
  const lat = track.lat[index];
  const lon = track.lon[index];
  if (lat === undefined || lon === undefined) throw new Error(`Indice hors trace : ${index}`);
  return { lat, lon };
}

/**
 * Progression d'un point de la trace : son indice multiplié par le pas, à
 * l'exception du dernier point dont la progression vaut la longueur totale.
 */
export function progressionAt(track: ComputedTrack, index: number): number {
  const value = track.cumulativeDistance[index];
  if (value === undefined) throw new Error(`Indice hors trace : ${index}`);
  return value;
}

/** Altitude d'un point, ou `null` lorsqu'elle est absente. */
export function altitudeAt(track: ComputedTrack, index: number): number | null {
  const value = track.alt[index];
  if (value === undefined || Number.isNaN(value)) return null;
  return value;
}

function readAt(array: Float64Array, index: number): number {
  const value = array[index];
  if (value === undefined) throw new Error(`Indice hors tableau : ${index}`);
  return value;
}

/**
 * Construit la trace de calcul à partir des points bruts, en appliquant dans
 * l'ordre les six étapes de EF-GPX-004. Aucune étape n'est itérée.
 */
export function buildComputedTrack(raw: RawTrack): ComputedTrack {
  const segmentGapIndices = new Set(raw.segmentStarts.filter((index) => index > 0));

  // Distances cumulées sur les points bruts, écarts entre segments compris.
  const rawCumulative = new Float64Array(raw.count);
  const segmentGaps: DistanceAnomaly[] = [];
  const pointAnomalies: DistanceAnomaly[] = [];
  const gapRanges: ProgressionRange[] = [];

  for (let i = 1; i < raw.count; i += 1) {
    const previous: Coordinate = { lat: readAt(raw.lat, i - 1), lon: readAt(raw.lon, i - 1) };
    const current: Coordinate = { lat: readAt(raw.lat, i), lon: readAt(raw.lon, i) };
    const step = distance(previous, current);
    rawCumulative[i] = readAt(rawCumulative, i - 1) + step;

    if (segmentGapIndices.has(i)) {
      // EF-GPX-002 : seul un écart supérieur à 1 km est un écart entre segments.
      if (step > SEGMENT_GAP_THRESHOLD_METERS) {
        segmentGaps.push({ afterProgressionMeters: readAt(rawCumulative, i - 1), distanceMeters: step });
        gapRanges.push({
          fromMeters: readAt(rawCumulative, i - 1),
          toMeters: readAt(rawCumulative, i),
        });
      }
    } else if (step > POINT_ANOMALY_THRESHOLD_METERS) {
      // EF-GPX-001 : écart anormal à l'intérieur d'un segment, signalé sans refus.
      pointAnomalies.push({ afterProgressionMeters: readAt(rawCumulative, i - 1), distanceMeters: step });
    }
  }

  const lengthMeters = raw.count > 0 ? readAt(rawCumulative, raw.count - 1) : 0;

  // Étape 1 — filtrage des altitudes brutes, sur une valeur isolée.
  const rawAltitude = Float64Array.from(raw.ele);
  let filteredPointCount = 0;
  for (let i = 0; i < raw.count; i += 1) {
    const value = readAt(rawAltitude, i);
    if (Number.isNaN(value)) continue;
    if (value < MIN_PLAUSIBLE_ALTITUDE || value > MAX_PLAUSIBLE_ALTITUDE) {
      rawAltitude[i] = Number.NaN;
      filteredPointCount += 1;
    }
  }

  // Étape 2 — rééchantillonnage tous les 100 m.
  const resampled = resample(raw, rawAltitude, rawCumulative, lengthMeters, gapRanges);
  const count = resampled.count;
  const { lat, lon, alt, cumulativeDistance, inGap } = resampled;

  // Étape 3 — détection des pics, sur les valeurs issues de l'étape 2.
  const beforePeakRemoval = Float64Array.from(alt);
  for (let i = 1; i < count - 1; i += 1) {
    const previous = readAt(beforePeakRemoval, i - 1);
    const current = readAt(beforePeakRemoval, i);
    const next = readAt(beforePeakRemoval, i + 1);
    if (Number.isNaN(previous) || Number.isNaN(current) || Number.isNaN(next)) continue;
    const risesAbove = current - previous > PEAK_THRESHOLD_METERS && current - next > PEAK_THRESHOLD_METERS;
    const fallsBelow = previous - current > PEAK_THRESHOLD_METERS && next - current > PEAK_THRESHOLD_METERS;
    if (risesAbove || fallsBelow) {
      alt[i] = Number.NaN;
      filteredPointCount += 1;
    }
  }

  // Étape 4 — comblement des trous encadrés et courts.
  const interpolatedPointCount = fillAltitudeHoles(alt, cumulativeDistance, inGap, count);

  // Étape 5 — couverture, évaluée sur le résultat de l'étape 4.
  const elevation = summarizeElevation(alt, cumulativeDistance, inGap, count, {
    interpolatedPointCount,
    filteredPointCount,
  });

  // Étape 6 — pentes et D+.
  const grade = new Float64Array(count);
  const cumulativeGain = new Float64Array(count);
  let gain = 0;
  for (let i = 1; i < count; i += 1) {
    const span = readAt(cumulativeDistance, i) - readAt(cumulativeDistance, i - 1);
    const current = readAt(alt, i);
    const previous = readAt(alt, i - 1);
    if (span > 0 && !Number.isNaN(current) && !Number.isNaN(previous)) {
      const rawGrade = ((current - previous) / span) * 100;
      grade[i] = Math.min(MAX_ABSOLUTE_GRADE_PERCENT, Math.max(-MAX_ABSOLUTE_GRADE_PERCENT, rawGrade));
      if (current > previous) gain += current - previous;
    } else {
      grade[i] = 0;
    }
    cumulativeGain[i] = gain;
  }

  return {
    lat,
    lon,
    alt,
    cumulativeDistance,
    cumulativeGain,
    grade,
    count,
    lengthMeters,
    gapRanges,
    segmentGaps,
    pointAnomalies,
    elevation,
    elevationGainMeters: elevation.available ? gain : null,
  };
}

interface ResampleResult {
  readonly lat: Float64Array;
  readonly lon: Float64Array;
  readonly alt: Float64Array;
  readonly cumulativeDistance: Float64Array;
  readonly inGap: Uint8Array;
  readonly count: number;
}

function isInsideGap(progression: number, gapRanges: readonly ProgressionRange[]): boolean {
  for (const range of gapRanges) {
    if (progression > range.fromMeters && progression < range.toMeters) return true;
  }
  return false;
}

function resample(
  raw: RawTrack,
  rawAltitude: Float64Array,
  rawCumulative: Float64Array,
  lengthMeters: number,
  gapRanges: readonly ProgressionRange[],
): ResampleResult {
  const stepCount = Math.floor(lengthMeters / RESAMPLE_STEP_METERS);
  const remainder = lengthMeters - stepCount * RESAMPLE_STEP_METERS;
  const count = stepCount + 1 + (remainder > 1e-9 ? 1 : 0);

  const lat = new Float64Array(count);
  const lon = new Float64Array(count);
  const alt = new Float64Array(count);
  const cumulativeDistance = new Float64Array(count);
  const inGap = new Uint8Array(count);

  let cursor = 1;
  for (let k = 0; k < count; k += 1) {
    const progression = k < count - 1 ? k * RESAMPLE_STEP_METERS : lengthMeters;
    cumulativeDistance[k] = progression;

    while (cursor < raw.count - 1 && readAt(rawCumulative, cursor) < progression) cursor += 1;

    const before = cursor - 1;
    const after = cursor;
    const spanStart = readAt(rawCumulative, before);
    const spanEnd = readAt(rawCumulative, after);
    const span = spanEnd - spanStart;
    const t = span > 0 ? (progression - spanStart) / span : 0;

    const a: Coordinate = { lat: readAt(raw.lat, before), lon: readAt(raw.lon, before) };
    const b: Coordinate = { lat: readAt(raw.lat, after), lon: readAt(raw.lon, after) };
    const position = interpolate(a, b, Math.min(1, Math.max(0, t)));
    lat[k] = position.lat;
    lon[k] = position.lon;

    if (isInsideGap(progression, gapRanges)) {
      // Les points d'un écart entre segments sont dépourvus d'altitude par
      // construction et ne sont jamais interpolés.
      inGap[k] = 1;
      alt[k] = Number.NaN;
      continue;
    }

    alt[k] = interpolateAltitude(rawAltitude, before, after, t, span);
  }

  return { lat, lon, alt, cumulativeDistance, inGap, count };
}

function interpolateAltitude(
  rawAltitude: Float64Array,
  before: number,
  after: number,
  t: number,
  span: number,
): number {
  const altitudeBefore = readAt(rawAltitude, before);
  const altitudeAfter = readAt(rawAltitude, after);

  // Le point rééchantillonné coïncide avec un point brut : aucune
  // interpolation n'est nécessaire, sa propre altitude fait foi.
  if (t <= 0) return altitudeBefore;
  if (t >= 1) return altitudeAfter;

  if (Number.isNaN(altitudeBefore) || Number.isNaN(altitudeAfter)) return Number.NaN;
  if (span > MAX_ALTITUDE_INTERPOLATION_METERS) return Number.NaN;
  return altitudeBefore + (altitudeAfter - altitudeBefore) * t;
}

/**
 * Étape 4. Un point dépourvu d'altitude reçoit une altitude par interpolation
 * entre les points connus qui l'encadrent, lorsque ceux-ci sont séparés d'au
 * plus 1 km de parcours. Les points d'un écart entre segments sont exclus.
 */
function fillAltitudeHoles(
  alt: Float64Array,
  cumulativeDistance: Float64Array,
  inGap: Uint8Array,
  count: number,
): number {
  let filled = 0;
  let index = 0;
  while (index < count) {
    if (!Number.isNaN(readAt(alt, index))) {
      index += 1;
      continue;
    }

    let end = index;
    while (end < count && Number.isNaN(readAt(alt, end))) end += 1;

    const before = index - 1;
    const after = end;
    const bounded = before >= 0 && after < count;
    if (bounded) {
      const from = readAt(cumulativeDistance, before);
      const to = readAt(cumulativeDistance, after);
      const containsGapPoint = containsGap(inGap, index, end);
      if (!containsGapPoint && to - from <= MAX_ALTITUDE_INTERPOLATION_METERS) {
        const altitudeFrom = readAt(alt, before);
        const altitudeTo = readAt(alt, after);
        const span = to - from;
        for (let i = index; i < end; i += 1) {
          const ratio = span > 0 ? (readAt(cumulativeDistance, i) - from) / span : 0;
          alt[i] = altitudeFrom + (altitudeTo - altitudeFrom) * ratio;
          filled += 1;
        }
      }
    }

    index = end;
  }
  return filled;
}

function containsGap(inGap: Uint8Array, from: number, to: number): boolean {
  for (let i = from; i < to; i += 1) {
    if (inGap[i] === 1) return true;
  }
  return false;
}

function summarizeElevation(
  alt: Float64Array,
  cumulativeDistance: Float64Array,
  inGap: Uint8Array,
  count: number,
  counts: { interpolatedPointCount: number; filteredPointCount: number },
): ElevationSummary {
  let considered = 0;
  let known = 0;
  let maxGapMeters = 0;
  let runStart = -1;

  const closeRun = (endExclusive: number): void => {
    if (runStart < 0) return;
    const from = readAt(cumulativeDistance, runStart);
    const to = readAt(cumulativeDistance, endExclusive - 1);
    maxGapMeters = Math.max(maxGapMeters, to - from + RESAMPLE_STEP_METERS);
    runStart = -1;
  };

  for (let i = 0; i < count; i += 1) {
    if (inGap[i] === 1) {
      // Exclus du dénominateur : ils ne dégradent pas la couverture.
      closeRun(i);
      continue;
    }
    considered += 1;
    if (Number.isNaN(readAt(alt, i))) {
      if (runStart < 0) runStart = i;
    } else {
      known += 1;
      closeRun(i);
    }
  }
  closeRun(count);

  const coveragePercent = considered > 0 ? (known / considered) * 100 : 0;
  const available =
    coveragePercent >= MIN_ELEVATION_COVERAGE_PERCENT && maxGapMeters <= MAX_ELEVATION_HOLE_METERS;

  return {
    available,
    coveragePercent,
    maxGapMeters,
    interpolatedPointCount: counts.interpolatedPointCount,
    filteredPointCount: counts.filteredPointCount,
  };
}

/**
 * Coordonnée à une progression donnée, par interpolation sur la trace de
 * calcul. Sert à la création d'un POI par progression (EF-POI-004).
 */
export function coordinateAtProgression(track: ComputedTrack, progressionMeters: number): Coordinate {
  const clamped = Math.min(track.lengthMeters, Math.max(0, progressionMeters));
  const index = indexBeforeProgression(track, clamped);
  if (index >= track.count - 1) return coordinateAt(track, track.count - 1);
  const from = progressionAt(track, index);
  const to = progressionAt(track, index + 1);
  const span = to - from;
  const t = span > 0 ? (clamped - from) / span : 0;
  return interpolate(coordinateAt(track, index), coordinateAt(track, index + 1), t);
}

/** Plus grand indice dont la progression est inférieure ou égale à la valeur. */
export function indexBeforeProgression(track: ComputedTrack, progressionMeters: number): number {
  let low = 0;
  let high = track.count - 1;
  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    if (progressionAt(track, middle) <= progressionMeters) low = middle;
    else high = middle - 1;
  }
  return low;
}

/** D+ cumulé à une progression donnée, par interpolation dans le pas. */
export function elevationGainAtProgression(track: ComputedTrack, progressionMeters: number): number {
  const clamped = Math.min(track.lengthMeters, Math.max(0, progressionMeters));
  const index = indexBeforeProgression(track, clamped);
  if (index >= track.count - 1) return readAt(track.cumulativeGain, track.count - 1);
  const from = progressionAt(track, index);
  const to = progressionAt(track, index + 1);
  const span = to - from;
  const t = span > 0 ? (clamped - from) / span : 0;
  const gainFrom = readAt(track.cumulativeGain, index);
  const gainTo = readAt(track.cumulativeGain, index + 1);
  return gainFrom + (gainTo - gainFrom) * t;
}

/**
 * Reconstruit une trace de calcul à partir des points enregistrés dans
 * `route.points` (annexe A).
 *
 * Les points enregistrés **sont déjà** la trace de calcul : aucun
 * rééchantillonnage n'est réappliqué. Seuls les tableaux dérivés — distances
 * cumulées, pentes, D+ cumulé — sont reconstruits, conformément à EF-CAL-011 et
 * à la règle d'indexation de l'annexe A.
 */
export function rehydrateComputedTrack(input: {
  readonly lat: Float64Array;
  readonly lon: Float64Array;
  readonly alt: Float64Array;
  readonly count: number;
  readonly lengthMeters: number;
  readonly segmentGaps: readonly DistanceAnomaly[];
  readonly pointAnomalies: readonly DistanceAnomaly[];
  readonly elevation: ElevationSummary;
  readonly elevationGainMeters: number | null;
}): ComputedTrack {
  const { count } = input;
  const cumulativeDistance = new Float64Array(count);
  for (let i = 0; i < count; i += 1) {
    cumulativeDistance[i] = i < count - 1 ? i * RESAMPLE_STEP_METERS : input.lengthMeters;
  }

  const grade = new Float64Array(count);
  const cumulativeGain = new Float64Array(count);
  let gain = 0;
  for (let i = 1; i < count; i += 1) {
    const span = readAt(cumulativeDistance, i) - readAt(cumulativeDistance, i - 1);
    const current = readAt(input.alt, i);
    const previous = readAt(input.alt, i - 1);
    if (span > 0 && !Number.isNaN(current) && !Number.isNaN(previous)) {
      const rawGrade = ((current - previous) / span) * 100;
      grade[i] = Math.min(MAX_ABSOLUTE_GRADE_PERCENT, Math.max(-MAX_ABSOLUTE_GRADE_PERCENT, rawGrade));
      if (current > previous) gain += current - previous;
    }
    cumulativeGain[i] = gain;
  }

  const gapRanges: ProgressionRange[] = input.segmentGaps.map((segmentGap) => ({
    fromMeters: segmentGap.afterProgressionMeters,
    toMeters: segmentGap.afterProgressionMeters + segmentGap.distanceMeters,
  }));

  return {
    lat: input.lat,
    lon: input.lon,
    alt: input.alt,
    cumulativeDistance,
    cumulativeGain,
    grade,
    count,
    lengthMeters: input.lengthMeters,
    gapRanges,
    segmentGaps: input.segmentGaps,
    pointAnomalies: input.pointAnomalies,
    elevation: input.elevation,
    elevationGainMeters: input.elevationGainMeters,
  };
}
